import { LuIcon, Text } from "@overmap-ai/blocks"
import { EMPTY_ARRAY, type IssueTypeStatus, type Stored, toUuidArray } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { IssueTypeStatusMultiSelect } from "../../../components/DropdownMultiSelects/IssueTypeStatusMultiSelect"
import { IssueTypeStatusSelect } from "../../../components/DropdownSelects/IssueTypeStatusSelect"
import { IssueTypeStatusIcon, IssueTypeStatusIcons } from "../../../components/Icons/IssueTypeStatusIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { IssueTypeStatusFilterDef } from "./IssueTypeStatusFilterDef"

type IssueTypeStatusFilterCellProps = FilterDefRenderProps<IssueTypeStatusFilterDef>

export const IssueTypeStatusFilterCell = (props: IssueTypeStatusFilterCellProps) => {
	const { filter, filterDef, filterManager } = props

	const { size } = use(FilterRendererContext)

	const issueTypeStatuses = filterDef.issueTypeStatuses

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedIssueTypeStatus = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return issueTypeStatuses.find(({ uuid }) => uuid === filterValue)
	}, [issueTypeStatuses, filterValue, filterDef.modifiers.equals])

	const selectedIssueTypeStatuses = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined
		const set = new Set(filterValue)
		return issueTypeStatuses.filter(({ uuid }) => set.has(uuid))
	}, [issueTypeStatuses, filterValue, filterDef.modifiers.includes])

	const handleValueChange = useCallback(
		(value?: Stored<IssueTypeStatus>) => {
			filter.setFilterValue(value?.uuid)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(values: Stored<IssueTypeStatus>[]) => {
			filter.setFilterValue(values.length > 0 ? toUuidArray(values) : undefined)
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsModifierFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<IssueTypeStatusSelect
						size={size}
						required={false}
						value={selectedIssueTypeStatus}
						onValueChange={handleValueChange}
						issueTypeStatuses={issueTypeStatuses}
					>
						<FilterCellButton className="truncate">
							{equalsModifierFilterValue !== undefined ? (
								<>
									<IssueTypeStatusIcon issueTypeStatusId={equalsModifierFilterValue} />
									<Text>{selectedIssueTypeStatus?.name}</Text>
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</IssueTypeStatusSelect>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedIssueTypeStatuses ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<IssueTypeStatusIcon issueTypeStatusId={selectedIssueTypeStatuses![0].uuid} />
									<Text className="truncate">{selectedIssueTypeStatuses![0].name}</Text>
								</>
							)
						default:
							return (
								<>
									<IssueTypeStatusIcons
										issueTypeStatusIds={toUuidArray(selectedIssueTypeStatuses!)}
									/>
									{`${selectedIssueTypeStatuses!.length} statuses`}
								</>
							)
					}
				}

				return (
					<IssueTypeStatusMultiSelect
						size={size}
						values={selectedIssueTypeStatuses ?? EMPTY_ARRAY}
						onValuesChange={handleValuesChange}
						issueTypeStatuses={issueTypeStatuses}
					>
						<FilterCellButton className="truncate">
							<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
						</FilterCellButton>
					</IssueTypeStatusMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.status} />
				Status
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
